import React, { forwardRef } from 'react';
import {
  Form,
  FormProps,
  FormButtonProps,
  FormFieldProps,
  FormInputProps,
  FormRadioProps,
  FormSelectProps,
  FormTextAreaProps,
  FormGroupProps,
  FormCheckboxProps,
  FormDropdownProps,
} from 'semantic-ui-react';
import {
  Controller,
  SubmitErrorHandler,
  useForm,
  UseFormReturn,
  UseFormProps,
  FormProvider,
  useFormContext,
  FieldValues,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AnyObjectSchema, Asserts } from 'yup';

export type IATMFormOnSubmit<T extends FieldValues> = (
  data: T,
  formProps: UseFormReturn<T>,
  event?: React.BaseSyntheticEvent
) => any | Promise<any>;

type ISchema = AnyObjectSchema;
/**
 * @reference https://react-hook-form.com/get-started/#Applyvalidation
 */
export type IATMFormProps<
  T extends ISchema,
  S extends FieldValues = Asserts<T>
> = Omit<FormProps, 'onSubmit'> &
  UseFormProps<S> & {
    validationSchema?: T;
    onSubmit?: IATMFormOnSubmit<S>;
    onError?: SubmitErrorHandler<S>;
    render?: React.ReactNode | ((form: UseFormReturn<S>) => React.ReactNode);
    children?: React.ReactNode | ((form: UseFormReturn<S>) => React.ReactNode);
  };

export type IATMFormController = Parameters<typeof Controller>[number];
export type IATMFormButton = FormButtonProps;
export type IATMFormFieldProps = FormFieldProps;
export type IATMFormInputProps = FormInputProps;
export type IATMFormRadioProps = FormRadioProps;
export type IATMFormSelectProps = FormSelectProps;
export type IATMFormTextAreaProps = FormTextAreaProps;
export type IATMFormGroupProps = FormGroupProps;
export type IATMFormCheckboxProps = FormCheckboxProps;
export type IATMFormDropdownProps = FormDropdownProps;

const SForm = <T extends ISchema>(
  {
    formRef,
    render,
    children,
    onSubmit,
    onError,
    // react-hook-form options
    validationSchema,
    mode = 'onChange',
    reValidateMode = 'onChange',
    defaultValues,
    resolver,
    context,
    shouldFocusError,
    shouldUnregister,
    criteriaMode,
    ...props
  }: React.PropsWithChildren<IATMFormProps<T>>,
  ref: any
) => {
  const formProps = useForm({
    resolver: validationSchema ? yupResolver(validationSchema) : resolver,
    mode,
    reValidateMode,
    defaultValues,
    context,
    shouldFocusError,
    shouldUnregister,
    criteriaMode,
  });

  const renderChildren = render
    ? render instanceof Function
      ? render(formProps)
      : render
    : typeof children === 'function'
      ? children(formProps)
      : children;

  return (
    <Form
      ref={ref}
      {...props}
      onSubmit={
        onSubmit &&
        formProps.handleSubmit(
          (data, event) => onSubmit(data, formProps, event),
          onError
        )
      }
    >
      {renderChildren}
    </Form>
  );
};

type IATMForm = (<T extends ISchema>(
  props: IATMFormProps<T> & { ref?: React.RefObject<HTMLFormElement> }
) => ReturnType<typeof SForm>) & {
  Controller: typeof Controller;
  Button: typeof Form.Button;
  Field: typeof Form.Field;
  Input: typeof Form.Input;
  Radio: typeof Form.Radio;
  Select: typeof Form.Select;
  TextArea: typeof Form.TextArea;
  Group: typeof Form.Group;
  Checkbox: typeof Form.Checkbox;
  Dropdown: typeof Form.Dropdown;
};

const ATMForm = forwardRef(SForm) as unknown as IATMForm;

ATMForm.Controller = Controller;
ATMForm.Button = Form.Button;
ATMForm.Field = Form.Field;
ATMForm.Input = Form.Input;
ATMForm.Radio = Form.Radio;
ATMForm.Select = Form.Select;
ATMForm.TextArea = Form.TextArea;
ATMForm.Group = Form.Group;
ATMForm.Checkbox = Form.Checkbox;
ATMForm.Dropdown = Form.Dropdown;

export const ATMFormProvider = FormProvider;
export const useATMFormContext = useFormContext;

export { ATMForm };
